/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-step-logo-text-builder.js — Text logo builder (Phase 2.5 rebuild)
 *
 * Doc 6 §11.1 step id `logo_text_builder`. Sub-step accessed via the
 * logo_picker step's text-pivot button.
 *
 * Phase 2.5 layout per mockups/step5a-logo-text-builder-{desktop,mobile}.html:
 *   1. .jrs-preview — live preview tile (dark/light background, text in
 *      selected colour and font)
 *   2. .jrs-text-lines fieldset — up to 4 lines, lines 2/3/4 revealed via
 *      "Add another line" link
 *   3. .jrs-field--font — native <select> (Standard / Italic) — replaces
 *      the previous 4-radio-card pattern (Standard/Italic/Bold/Script)
 *   4. .jrs-field--colour — sub-labelled section:
 *      4a. "Standard colours" + .jrs-colour-grid (10 swatches with names
 *          + tick overlays)
 *      4b. "Or pick a custom colour" + .jrs-custom-colour widget
 *          (native color input swatch + hex text input + error line)
 *
 * State paths per Doc 6 §5.3 (UNCHANGED):
 *   state.draft.text_logo = { lines: [...], font, color }
 *   state.draft.logo_assignments[<positionId>] = { source: 'text', logoId }
 *
 * Telemetry preserved (Doc 3 v0.2):
 *   pdp_customizer_text_logo_line_added per §4.13 on "Add line" click
 *   pdp_customizer_decision with decision_type='text_logo_*' on field
 *   changes; colour_source='swatch'|'custom' sub-field on color decisions
 *
 * Mockup font options: 2 (Standard, Italic). Previous controller had 4
 * (added Bold + Script as guesses). Phase 2.5 aligns with the locked
 * mockup; merchants can add fonts later via translation overrides.
 *
 * Mockup colour swatches: 10 named (white, black, silver, red, green,
 * orange, blue, purple, grey, yellow) with hex values defined inline in
 * --logo-* CSS variables. The variables live inside the .jrs-step--text-
 * builder scope in jrs-v2-steps.css.
 */

window.JRS = window.JRS || {};
window.JRS.Step = window.JRS.Step || {};

(function (JRS) {
  'use strict';

  var STEP_ID = 'logo_text_builder';

  var _mountEl = null;
  var _listeners = [];
  var _typingTimer = null;
  var TYPING_DEBOUNCE_MS = 400;

  // 10 named swatches matching the mockup. Each has a hex value used both
  // for the swatch background (via inline style) and for the preview text
  // color when this swatch is selected. The CSS `--logo-<name>` variables
  // exist for visual reference but the controller writes hex values
  // directly to keep the contract identical to non-swatch (custom hex)
  // selections.
  var SWATCHES = [
    { name: 'White',  hex: '#FFFFFF' },
    { name: 'Black',  hex: '#1A1A1A' },
    { name: 'Silver', hex: '#C0C0C0' },
    { name: 'Red',    hex: '#DC2626' },
    { name: 'Green',  hex: '#059669' },
    { name: 'Orange', hex: '#FC5A2A' },
    { name: 'Blue',   hex: '#1D4ED8' },
    { name: 'Purple', hex: '#7C3AED' },
    { name: 'Grey',   hex: '#6B7280' },
    { name: 'Yellow', hex: '#FACC15' }
  ];

  var FONTS = [
    { id: 'Standard', label: 'Standard' },
    { id: 'Italic',   label: 'Italic' }
  ];

  // ---------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------

  function onEnter(state, context) {
    _mountEl = context.mountEl;
    if (!_mountEl) return;

    JRS.CustomizerPopup.setReassurance(
      'Up to 4 lines.'
    );
    JRS.CustomizerPopupState.dispatch({ type: 'HIDE_SUMMARY' });

    var existing = (state.draft && state.draft.text_logo) || {};
    var lines = (existing.lines && existing.lines.length) ? existing.lines.slice() : [''];
    while (lines.length < 1) lines.push('');

    _render(lines, existing.font || 'Standard', existing.color || '#1A1A1A');
    _bindInteractions();
    _syncCta(true); // chrome-footer Continue CTA (enabled once line 1 has text)
  }

  function onExit(state, context) {
    if (_typingTimer) { clearTimeout(_typingTimer); _typingTimer = null; }
    _unbindListeners();
    _mountEl = null;
  }

  function onValidate(state, context) {
    var tl = (state.draft && state.draft.text_logo) || null;
    if (!tl || !tl.lines || !tl.lines.length || !tl.lines[0] || !String(tl.lines[0]).replace(/\s+/g, '').length) {
      _showError('Please add at least one line of text.');
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_validation_error', {
        step_id: STEP_ID,
        field_id: 'text_logo.lines[0]',
        reason: 'line_1_required'
      });
      return { valid: false, errors: { text_logo: 'line_1_required' } };
    }
    if (!tl.font) {
      _showError('Please pick a font.');
      return { valid: false, errors: { text_logo: 'font_required' } };
    }
    if (!tl.color) {
      _showError('Please pick a colour.');
      return { valid: false, errors: { text_logo: 'color_required' } };
    }
    _clearError();
    return { valid: true };
  }

  // Drive the shell's chrome-footer Continue CTA: enabled once line 1 has
  // non-whitespace text (matches onValidate's primary gate; font + colour
  // always have defaults). initial=true sets the button (with onClick); later
  // calls patch the disabled state as the user types.
  function _syncCta(initial) {
    var pop = JRS.CustomizerPopup;
    if (!pop) return;
    var tl = (JRS.CustomizerPopupState.getState().draft || {}).text_logo || {};
    var lines = tl.lines || [];
    var hasText = !!(lines[0] && String(lines[0]).replace(/\s+/g, '').length);
    // Disabled, the button says what's missing instead of greying out with no
    // reason — same ternary as logo_picker's "Add a logo to continue". The
    // enabled label describes the action, not a destination: this step always
    // returns to logo_picker (see _onContinueClick), so a label naming review
    // would be untrue. The label is patched on every keystroke sync so it swaps
    // as text appears.
    var label = hasText ? 'Use this text logo' : 'Add your text to continue';
    if (initial && typeof pop.setPrimaryCta === 'function') {
      pop.setPrimaryCta({ label: label, disabled: !hasText, onClick: _onContinueClick });
    } else if (typeof pop.updatePrimaryCta === 'function') {
      pop.updatePrimaryCta({ label: label, disabled: !hasText });
    }
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  function _render(lines, font, color) {
    var visibleLineCount = lines.length;

    var lineInputsHtml = '';
    for (var i = 0; i < 4; i++) {
      var hidden = i >= visibleLineCount;
      var value = i < visibleLineCount ? lines[i] : '';
      lineInputsHtml +=
        '<div class="jrs-text-line"' + (hidden ? ' hidden' : '') + ' data-jrs-line-index="' + i + '">' +
        '  <input class="jrs-field__input" type="text"' +
        '         data-jrs-action="text-line-change"' +
        '         data-jrs-line-index="' + i + '"' +
        '         maxlength="40"' +
        '         placeholder="e.g. JRS Industries"' +
        '         aria-label="Text line ' + (i + 1) + '"' +
        '         value="' + _escape(value) + '">' +
        '</div>';
    }

    var addLineHtml = visibleLineCount < 4
      ? '<button type="button" class="jrs-text-lines__add" data-jrs-action="add-line" data-jrs-reveal="' + visibleLineCount + '">Add another line</button>'
      : '';

    var fontOptionsHtml = '';
    for (var f = 0; f < FONTS.length; f++) {
      fontOptionsHtml +=
        '<option value="' + _escape(FONTS[f].id) + '"' +
          (font === FONTS[f].id ? ' selected' : '') + '>' +
          _escape(FONTS[f].label) +
        '</option>';
    }

    var swatchesHtml = '';
    for (var s = 0; s < SWATCHES.length; s++) {
      var sw = SWATCHES[s];
      var checked = (color === sw.hex);
      var dataName = sw.name.toLowerCase();
      swatchesHtml +=
        '<button type="button" class="jrs-colour-swatch"' +
        '        role="radio" aria-checked="' + (checked ? 'true' : 'false') + '"' +
        '        data-jrs-action="swatch-click"' +
        '        data-jrs-value="' + _escape(sw.hex) + '"' +
        '        data-jrs-label="' + _escape(sw.name) + '"' +
        '        data-colour="' + _escape(dataName) + '"' +
        '        style="background-color: ' + _escape(sw.hex) + ';">' +
        '  <span class="jrs-colour-swatch__name">' + _escape(sw.name) + '</span>' +
        '  <span class="jrs-colour-swatch__tick" aria-hidden="true">' +
        '    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12 L10 18 L20 6"/></svg>' +
        '  </span>' +
        '</button>';
    }

    var isCustomColor = !_findSwatchByHex(color);
    var customHexValue = isCustomColor ? color : '';
    var previewBg = _previewBgForColor(color);

    _mountEl.innerHTML =
      '<div class="jrs-step jrs-step--text-builder" data-jrs-step="logo_text_builder">' +
      '  <header class="jrs-step__header">' +
      '    <h2 class="jrs-step__title">Build your text logo</h2>' +
      '  </header>' +
      '  <section class="jrs-step__body">' +
           _renderPreview(lines, font, color, previewBg) +
      '    <fieldset class="jrs-text-lines">' +
      '      <legend class="jrs-field__label">Text</legend>' +
             lineInputsHtml +
             addLineHtml +
      '    </fieldset>' +
      '    <div class="jrs-field jrs-field--font">' +
      '      <label class="jrs-field__label" for="jrs-font-select">Font</label>' +
      '      <select class="jrs-field__select" id="jrs-font-select"' +
      '              data-jrs-action="font-change">' +
              fontOptionsHtml +
      '      </select>' +
      '    </div>' +
      '    <div class="jrs-field jrs-field--colour">' +
      '      <span class="jrs-field__label" id="jrs-colour-label">Colour</span>' +
      '      <span class="jrs-colour-sublabel">Standard colours</span>' +
      '      <div class="jrs-colour-grid" role="radiogroup" aria-labelledby="jrs-colour-label">' +
              swatchesHtml +
      '      </div>' +
      '      <span class="jrs-colour-sublabel">Or pick a custom colour</span>' +
      '      <div class="jrs-custom-colour">' +
      '        <label class="jrs-custom-colour__swatch"' +
                  (isCustomColor ? ' aria-checked="true"' : ' data-state="empty"') +
      '              aria-label="Open colour picker">' +
      '          <span class="jrs-custom-colour__fill" aria-hidden="true"' +
                    (isCustomColor ? ' style="background: ' + _escape(color) + ';"' : '') + '></span>' +
      '          <input class="jrs-custom-colour__input" type="color"' +
      '                 value="' + _escape(isCustomColor ? color : '#1A1A1A') + '"' +
      '                 data-jrs-action="custom-color-picker"' +
      '                 aria-label="Custom colour picker">' +
      '        </label>' +
      '        <input class="jrs-custom-colour__hex" type="text"' +
      '               placeholder="#000000"' +
      '               maxlength="7"' +
      '               value="' + _escape(customHexValue) + '"' +
      '               data-jrs-action="custom-hex-input"' +
      '               aria-label="Custom hex code">' +
      '      </div>' +
      '      <p class="jrs-custom-colour__error" data-jrs-custom-colour-error></p>' +
      '      <p class="jrs-custom-colour__caveat">' +
      '        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="8" cy="8" r="6.5"/><path d="M8 4 L8 9 M8 11.5 L8 12"/></svg>' +
      '        We\'ll match the closest thread or ink colour and confirm it on your proof.' +
      '      </p>' +
      '    </div>' +
      '    <p data-jrs-text-error class="jrs-step__error" hidden role="alert"></p>' +
      '  </section>' +
      '</div>';
  }

  function _renderPreview(lines, font, color, previewBg) {
    var linesHtml = '';
    for (var i = 0; i < lines.length; i++) {
      if (!lines[i]) continue;
      linesHtml += '<span class="jrs-preview__text-line">' + _escape(lines[i]) + '</span>';
    }
    if (!linesHtml) linesHtml = '<span class="jrs-preview__text-line jrs-preview__text-line--placeholder">Your text</span>';
    return (
      '<div class="jrs-preview" role="region" aria-label="Live preview">' +
      '  <span class="jrs-preview__label">Preview</span>' +
      '  <div class="jrs-preview__tile" data-jrs-preview-tile style="background: ' + _escape(previewBg) + ';">' +
      '    <div class="jrs-preview__text" data-jrs-preview-text data-font="' + _escape(font.toLowerCase()) + '" style="color: ' + _escape(color) + ';">' +
            linesHtml +
      '    </div>' +
      '  </div>' +
      '  <p class="jrs-preview__caveat">Preview only. We\'ll send a design proof before production.</p>' +
      '</div>'
    );
  }

  function _findSwatchByHex(hex) {
    if (!hex) return null;
    for (var i = 0; i < SWATCHES.length; i++) {
      if (SWATCHES[i].hex.toUpperCase() === String(hex).toUpperCase()) return SWATCHES[i];
    }
    return null;
  }

  // Pick a preview-tile background that contrasts with the text colour:
  // light embroidery colours → dark tile; dark colours → white tile.
  function _previewBgForColor(hex) {
    if (!hex) return '#FFFFFF';
    var h = String(hex).trim();
    if (h.charAt(0) !== '#') h = '#' + h;
    if (h.length === 4) {
      h = '#' + h.charAt(1) + h.charAt(1) + h.charAt(2) + h.charAt(2) + h.charAt(3) + h.charAt(3);
    }
    if (!/^#[0-9A-Fa-f]{6}$/.test(h)) return '#FFFFFF';
    var r = parseInt(h.slice(1, 3), 16);
    var g = parseInt(h.slice(3, 5), 16);
    var b = parseInt(h.slice(5, 7), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b)) return '#FFFFFF';
    function lin(c) {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    var lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    return lum > 0.45 ? '#1A1A1A' : '#FFFFFF';
  }

  function _showError(msg) {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-text-error]');
    if (err) { err.textContent = msg; err.hidden = false; }
  }

  function _clearError() {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-text-error]');
    if (err) { err.textContent = ''; err.hidden = true; }
  }

  function _setHexFieldError(msg) {
    if (!_mountEl) return;
    var err = _mountEl.querySelector('[data-jrs-custom-colour-error]');
    if (err) err.textContent = msg || '';
    var hexInput = _mountEl.querySelector('[data-jrs-action="custom-hex-input"]');
    if (hexInput) {
      if (msg) hexInput.setAttribute('aria-invalid', 'true');
      else hexInput.removeAttribute('aria-invalid');
    }
  }

  function _updatePreview() {
    if (!_mountEl) return;
    var draft = (JRS.CustomizerPopupState.getState().draft || {});
    var tl = draft.text_logo || {};
    var lines = tl.lines || [''];
    var color = tl.color || '#1A1A1A';
    var font = (tl.font || 'Standard').toLowerCase();

    var tile = _mountEl.querySelector('[data-jrs-preview-tile]');
    var text = _mountEl.querySelector('[data-jrs-preview-text]');
    if (!tile || !text) return;
    tile.style.background = _previewBgForColor(color);
    text.setAttribute('data-font', font);
    text.style.color = color;
    var html = '';
    for (var i = 0; i < lines.length; i++) {
      if (!lines[i]) continue;
      html += '<span class="jrs-preview__text-line">' + _escape(lines[i]) + '</span>';
    }
    if (!html) html = '<span class="jrs-preview__text-line jrs-preview__text-line--placeholder">Your text</span>';
    text.innerHTML = html;
  }

  function _updateSwatchSelection(hex) {
    if (!_mountEl) return;
    var swatches = _mountEl.querySelectorAll('[data-jrs-action="swatch-click"]');
    var matchHex = _findSwatchByHex(hex);
    for (var i = 0; i < swatches.length; i++) {
      var btnHex = swatches[i].getAttribute('data-jrs-value');
      var isChecked = matchHex && btnHex.toUpperCase() === matchHex.hex.toUpperCase();
      swatches[i].setAttribute('aria-checked', isChecked ? 'true' : 'false');
    }
    // Custom-colour swatch state
    var customSwatch = _mountEl.querySelector('.jrs-custom-colour__swatch');
    var customFill = _mountEl.querySelector('.jrs-custom-colour__fill');
    if (customSwatch && customFill) {
      if (matchHex) {
        customSwatch.removeAttribute('aria-checked');
        customSwatch.setAttribute('data-state', 'empty');
        customFill.style.background = '';
      } else if (hex) {
        customSwatch.setAttribute('aria-checked', 'true');
        customSwatch.removeAttribute('data-state');
        customFill.style.background = hex;
      }
    }
  }

  // ---------------------------------------------------------------------
  // Interactions
  // ---------------------------------------------------------------------

  function _bindInteractions() {
    if (!_mountEl) return;

    var lineInputs = _mountEl.querySelectorAll('[data-jrs-action="text-line-change"]');
    for (var i = 0; i < lineInputs.length; i++) _bind(lineInputs[i], 'input', _onTextLineChange);

    var addBtn = _mountEl.querySelector('[data-jrs-action="add-line"]');
    if (addBtn) _bind(addBtn, 'click', _onAddLineClick);

    var fontSelect = _mountEl.querySelector('[data-jrs-action="font-change"]');
    if (fontSelect) _bind(fontSelect, 'change', _onFontChange);

    var swatches = _mountEl.querySelectorAll('[data-jrs-action="swatch-click"]');
    for (var k = 0; k < swatches.length; k++) _bind(swatches[k], 'click', _onSwatchClick);

    var picker = _mountEl.querySelector('[data-jrs-action="custom-color-picker"]');
    if (picker) _bind(picker, 'input', _onCustomColorPickerChange);

    var hexInput = _mountEl.querySelector('[data-jrs-action="custom-hex-input"]');
    if (hexInput) _bind(hexInput, 'input', _onCustomHexInput);
  }

  function _onTextLineChange(e) {
    var input = e.currentTarget;
    var idx = parseInt(input.getAttribute('data-jrs-line-index'), 10);
    if (isNaN(idx)) return;

    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var tl = draft.text_logo || { lines: [], font: 'Standard', color: '#1A1A1A' };
    var lines = (tl.lines || []).slice();
    while (lines.length <= idx) lines.push('');
    lines[idx] = input.value;

    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'text_logo', value: _merge(tl, { lines: lines }) }
    });
    _updatePreview();
    _syncCta(false); // live enable/disable as line 1 gains/loses text

    if (_typingTimer) clearTimeout(_typingTimer);
    _typingTimer = setTimeout(function () {
      JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
        step_id: STEP_ID,
        decision_type: 'text_logo_text',
        decision_value: lines.join(' | '),
        line_count: lines.filter(function (l) { return l && String(l).length > 0; }).length
      });
    }, TYPING_DEBOUNCE_MS);
  }

  function _onAddLineClick(e) {
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var tl = draft.text_logo || { lines: [''], font: 'Standard', color: '#1A1A1A' };
    var lines = (tl.lines || []).slice();
    if (lines.length >= 4) return;
    lines.push('');
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'text_logo', value: _merge(tl, { lines: lines }) }
    });

    // Doc 3 v0.2 §4.13 — text_logo_line_added
    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_text_logo_line_added', {
      step_id: STEP_ID,
      line_number: lines.length
    });

    var revealedIdx = lines.length - 1;
    var revealed = _mountEl.querySelector('.jrs-text-line[data-jrs-line-index="' + revealedIdx + '"]');
    if (revealed) {
      revealed.hidden = false;
      var input = revealed.querySelector('input');
      if (input) input.focus();
    }
    if (lines.length >= 4 && e.currentTarget && e.currentTarget.parentNode) {
      e.currentTarget.parentNode.removeChild(e.currentTarget);
    } else if (e.currentTarget) {
      e.currentTarget.setAttribute('data-jrs-reveal', String(lines.length));
    }
  }

  function _onFontChange(e) {
    var value = e.currentTarget && e.currentTarget.value;
    if (!value) return;
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var tl = draft.text_logo || { lines: [''], font: 'Standard', color: '#1A1A1A' };
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'text_logo', value: _merge(tl, { font: value }) }
    });
    _updatePreview();
    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'text_logo_font',
      decision_value: value
    });
  }

  function _onSwatchClick(e) {
    var hex = e.currentTarget && e.currentTarget.getAttribute('data-jrs-value');
    var label = e.currentTarget && e.currentTarget.getAttribute('data-jrs-label');
    if (!hex) return;

    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var tl = draft.text_logo || { lines: [''], font: 'Standard', color: '#1A1A1A' };
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'text_logo', value: _merge(tl, { color: hex }) }
    });
    _updatePreview();
    _updateSwatchSelection(hex);
    _setHexFieldError(null);
    var hexField = _mountEl.querySelector('[data-jrs-action="custom-hex-input"]');
    if (hexField) hexField.value = '';

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'text_logo_color',
      decision_value: label || hex,
      colour_source: 'swatch'
    });
  }

  function _onCustomColorPickerChange(e) {
    var value = e.currentTarget && e.currentTarget.value;
    if (!value) return;
    _commitCustomColor(value);
    var hexField = _mountEl.querySelector('[data-jrs-action="custom-hex-input"]');
    if (hexField) hexField.value = value.toUpperCase();
  }

  function _onCustomHexInput(e) {
    var raw = e.currentTarget && e.currentTarget.value;
    if (typeof raw !== 'string') return;
    var trimmed = raw.replace(/\s+/g, '');
    if (trimmed.length === 0) {
      _setHexFieldError(null);
      return;
    }
    if (!/^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(trimmed)) {
      // Not a valid hex yet — show inline error but don't commit
      _setHexFieldError('Enter a valid hex like #FC5A2A.');
      return;
    }
    var normalised = trimmed.charAt(0) === '#' ? trimmed : ('#' + trimmed);
    _setHexFieldError(null);
    _commitCustomColor(normalised);
  }

  function _commitCustomColor(hex) {
    var draft = JRS.CustomizerPopupState.getState().draft || {};
    var tl = draft.text_logo || { lines: [''], font: 'Standard', color: '#1A1A1A' };
    JRS.CustomizerPopupState.dispatch({
      type: 'SET_DRAFT_FIELD',
      payload: { field: 'text_logo', value: _merge(tl, { color: hex }) }
    });
    _updatePreview();
    _updateSwatchSelection(hex);

    JRS.CustomizerPopup.fireAnalytics('pdp_customizer_decision', {
      step_id: STEP_ID,
      decision_type: 'text_logo_color',
      decision_value: hex,
      colour_source: 'custom'
    });
  }

  function _onContinueClick() {
    var state = JRS.CustomizerPopupState.getState();
    var result = onValidate(state, { trigger: 'primary_cta' });
    if (!result.valid) return;

    var draft = state.draft || {};
    var pid = _resolveCurrentPositionId(draft);

    if (pid) {
      var assignments = draft.logo_assignments || {};
      var next = {};
      for (var k in assignments) {
        if (Object.prototype.hasOwnProperty.call(assignments, k)) next[k] = assignments[k];
      }
      var textLogoId = (assignments[pid] && assignments[pid].source === 'text' && assignments[pid].logoId)
        ? assignments[pid].logoId
        : ((JRS.LogoService && typeof JRS.LogoService.mintLogoId === 'function')
            ? JRS.LogoService.mintLogoId() // ERP parity: control's numeric logo ids
            : 'text_' + Date.now());

      // 2026-05-20 Bug 4 fix — enrich the assignment with a previewText
      // pulled from the current text_logo lines so the review step's
      // logo-group thumb renders the actual text instead of the
      // placeholder "TEXT" label.
      var textLogo = draft.text_logo || {};
      var firstLine = (textLogo.lines && textLogo.lines.length > 0)
        ? String(textLogo.lines[0] || '')
        : '';

      var assignmentForPid = {
        source: 'text',
        logoId: textLogoId,
        previewText: firstLine
      };
      next[pid] = assignmentForPid;

      if (draft.logo_strategy === 'same_logo') {
        var positions = draft.positions || [];
        for (var i = 0; i < positions.length; i++) {
          next[positions[i]] = assignmentForPid;
        }
      }
      JRS.CustomizerPopupState.dispatch({
        type: 'SET_DRAFT_FIELD',
        payload: { field: 'logo_assignments', value: next }
      });
    }

    JRS.CustomizerPopup.goToStep('logo_picker', { trigger: 'primary_cta' });
  }

  function _resolveCurrentPositionId(draft) {
    var positions = draft.positions || [];
    // Honour the pin only while it is still a selected position. A review Edit
    // pins a position; swapping it out in the position picker used to leave the
    // pin on the removed position, so validation passed on its old logo and the
    // new position reached Confirm with none (logo_setup_missing).
    if (draft._current_position_for_logo &&
        positions.indexOf(draft._current_position_for_logo) !== -1) {
      return draft._current_position_for_logo;
    }
    if (positions.length === 0) return null;
    var cursor = typeof draft._position_cursor === 'number' ? draft._position_cursor : 0;
    if (cursor < 0 || cursor >= positions.length) cursor = 0;
    return positions[cursor];
  }

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function _bind(el, evt, fn) {
    el.addEventListener(evt, fn);
    _listeners.push({ el: el, evt: evt, fn: fn });
  }

  function _unbindListeners() {
    for (var i = 0; i < _listeners.length; i++) {
      var t = _listeners[i];
      try { t.el.removeEventListener(t.evt, t.fn); } catch (e) {}
    }
    _listeners = [];
  }

  function _escape(s) {
    if (typeof s !== 'string') return '';
    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function _merge(a, b) {
    var out = {};
    for (var k1 in a) {
      if (Object.prototype.hasOwnProperty.call(a, k1)) out[k1] = a[k1];
    }
    for (var k2 in b) {
      if (Object.prototype.hasOwnProperty.call(b, k2)) out[k2] = b[k2];
    }
    return out;
  }

  // ---------------------------------------------------------------------
  // Public API + register
  // ---------------------------------------------------------------------

  JRS.Step.LogoTextBuilder = {
    STEP_ID: STEP_ID,
    onEnter: onEnter,
    onExit: onExit,
    onValidate: onValidate
  };

  function _register() {
    if (window.JRS && JRS.CustomizerPopup && typeof JRS.CustomizerPopup.registerStep === 'function') {
      JRS.CustomizerPopup.registerStep(STEP_ID, {
        onEnter: onEnter,
        onExit: onExit,
        onValidate: onValidate
      });
    } else {
      window.JRS = window.JRS || {};
      window.JRS._pendingStepRegistrations = window.JRS._pendingStepRegistrations || [];
      window.JRS._pendingStepRegistrations.push({
        stepId: STEP_ID,
        definition: { onEnter: onEnter, onExit: onExit, onValidate: onValidate }
      });
    }
  }
  _register();
})(window.JRS);
